import React, { useEffect, useState, useCallback } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../config/firebase";
import { AuthContext } from "./authCore";
import type { User } from "firebase/auth";
import {
  signInWithGoogle,
  signInWithGitHub,
  signOutUser,
} from "../services/authService";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isManager, setIsManager] = useState<boolean>(false);

  const refreshClaims = useCallback(
    async (u: User | null, forceRefresh = false) => {
      if (!u) {
        setIsAdmin(false);
        setIsManager(false);
        return;
      }

      try {
        // Force refresh token from server if requested to get latest custom claims
        if (forceRefresh) {
          await u.getIdToken(true);
        }
        // Read ID token claims to detect admin/manager privileges.
        const idTokenResult = await u.getIdTokenResult();
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        setIsAdmin(Boolean(idTokenResult?.claims?.admin));
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        setIsManager(Boolean(idTokenResult?.claims?.manager));
      } catch (err) {
        // If token retrieval fails, fallback to non-admin/manager and log.
        // eslint-disable-next-line no-console
        console.warn("Failed to read ID token claims:", err);
        setIsAdmin(false);
        setIsManager(false);
      }
    },
    [],
  );

  useEffect(() => {
    // If firebase wasn't initialized (missing env vars), `auth` may be null/undefined.
    if (!auth) {
      // eslint-disable-next-line no-console
      console.warn(
        "Firebase auth not initialized; skipping onAuthStateChanged subscription.",
      );
      return;
    }
    const REDIRECT_KEY = "bytemonk_post_signin_redirect";
    const unsubscribe = onAuthStateChanged(auth as any, (currentUser) => {
      setUser(currentUser);
      void refreshClaims(currentUser);

      // If a redirect sign-in happened (or popup), and we have a stored target,
      // perform a one-time navigation to that path and remove the stored key.
      try {
        if (currentUser) {
          const stored = sessionStorage.getItem(REDIRECT_KEY);
          if (stored) {
            // remove before navigating to avoid loops
            sessionStorage.removeItem(REDIRECT_KEY);
            try {
              // Use location.assign so the router re-initializes correctly after redirect sign-in
              window.location.assign(stored);
            } catch (navErr) {
              if ((import.meta as any)?.env?.DEV) {
                // eslint-disable-next-line no-console
                console.warn(
                  "window.location.assign failed; falling back",
                  navErr,
                );
              }
              // fallback to setting href
              try {
                window.location.href = stored;
              } catch (hrefError) {
                if ((import.meta as any)?.env?.DEV) {
                  // eslint-disable-next-line no-console
                  console.warn(
                    "window.location.href fallback failed",
                    hrefError,
                  );
                }
              }
            }
          }
        }
      } catch (storageError) {
        if ((import.meta as any)?.env?.DEV) {
          // eslint-disable-next-line no-console
          console.warn("Session storage lookup failed", storageError);
        }
      }
    });

    return () => unsubscribe();
  }, [refreshClaims]);

  return (
    <AuthContext.Provider
      value={{
        user,
        signInWithGoogle,
        signInWithGitHub,
        signOut: signOutUser,
        isAdmin,
        isManager,
        refreshClaims: async () => refreshClaims(user, true),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
